import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faPause } from '@fortawesome/free-solid-svg-icons';

const RespControleOnHoldDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const showSuccessModalDismiss = useModalDismiss(() => setShowSuccessModal(false), { backdropClose: false, enabled: showSuccessModal });
  const [showErrorModal, setShowErrorModal] = useState(false);
  const showErrorModalDismiss = useModalDismiss(() => setShowErrorModal(false), { backdropClose: false, enabled: showErrorModal });
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const showConfirmModalDismiss = useModalDismiss(() => setShowConfirmModal(false), { backdropClose: false, enabled: showConfirmModal });
  const [copiedTel, setCopiedTel] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    raison: '',
    comment: '',
    action: 'validate',
    correction_applied: null,
    reject_reason: ''
  });

  useEffect(() => {
    fetchOnHoldCheck();
  }, [id]);

  const fetchOnHoldCheck = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/control/resp/on-hold/${id}`);

      if (response.data.success && response.data.data) {
        setCheck(response.data.data);
      } else {
        setErrorModalMessage('Soumission en attente non trouvee');
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error fetching on-hold check:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la recuperation de la soumission';
      setErrorModalMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTel = () => {
    const sub = check?.f2f_submission || check?.submission;
    const isF2f = check?.is_f2f;
    const valueToCopy = isF2f
      ? (sub?.payload_json?.SessionCode || sub?.payload_json?.sessioncode)
      : (sub?.payload_json?.TEL || sub?.payload_json?.Numero);
    if (valueToCopy) {
      navigator.clipboard.writeText(valueToCopy);
      setCopiedTel(true);
      setTimeout(() => setCopiedTel(false), 2000);
    }
  };

  const handlePreSubmit = () => {
    if (!formData.raison) {
      setErrorModalMessage('Veuillez selectionner une raison.');
      setShowErrorModal(true);
      return;
    }

    if (formData.action === 'hold') {
      // No other validation needed for hold
    } else if (formData.action === 'reject') {
      if (!formData.rating || formData.rating < 1) {
        setErrorModalMessage('Veuillez attribuer une note sur 5 etoiles (minimum 1 etoile).');
        setShowErrorModal(true);
        return;
      }
      if (!formData.reject_reason.trim()) {
        setErrorModalMessage('Veuillez fournir une raison pour le rejet');
        setShowErrorModal(true);
        return;
      }
    } else {
      if (!formData.rating || formData.rating < 1) {
        setErrorModalMessage('Veuillez attribuer une note sur 5 etoiles (minimum 1 etoile).');
        setShowErrorModal(true);
        return;
      }
      if (formData.correction_applied === null) {
        setErrorModalMessage('Veuillez indiquer si une correction a ete appliquee (Oui ou Non).');
        setShowErrorModal(true);
        return;
      }
      if (formData.correction_applied === true && !formData.comment.trim()) {
        setErrorModalMessage('Un commentaire est obligatoire lorsqu\'une correction est appliquee.');
        setShowErrorModal(true);
        return;
      }
    }

    setShowConfirmModal(true);
  };

  const handleSubmit = async () => {
    setShowConfirmModal(false);
    try {
      setSubmitting(true);
      await axios.post('/control/resp/check', {
        submission_id: check.f2f_submission_id ? null : check.submission_id,
        f2f_submission_id: check.f2f_submission_id || null,
        rating: formData.action === 'hold' ? null : formData.rating,
        raison: formData.raison,
        comment: formData.comment || null,
        action: formData.action,
        correction_applied: formData.action === 'hold' ? false : (formData.action === 'reject' ? false : formData.correction_applied),
        reject_reason: formData.reject_reason || null
      });

      setShowSuccessModal(true);

      // Navigate back to on-hold list after success
      setTimeout(() => {
        setShowSuccessModal(false);
        navigate('/resp-controle/on-hold');
      }, 2000);
    } catch (error) {
      console.error('Error submitting check:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.errors
        || 'Erreur lors de la soumission du controle';

      let errorText = '';
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        errorText = Object.values(errors).flat().join('\n');
      } else {
        errorText = typeof errorMessage === 'string' ? errorMessage : 'Erreur lors de la soumission du controle';
      }

      setErrorModalMessage(errorText);
      setShowErrorModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/resp-controle/on-hold');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!check) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-12 text-center max-w-2xl mx-auto">
            <div className="mb-6">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Soumission non trouvee
            </h2>
            <p className="text-gray-600 mb-8">
              Cette soumission n'existe plus ou n'est plus en attente.
            </p>
            <button
              onClick={handleBack}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retour a la liste
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isF2f = check?.is_f2f;
  const sub = isF2f ? check.f2f_submission : check.submission;
  const surveyLink = `https://defineresearch.online/index.php/admin/dataentry/sa/editdata/subaction/edit/surveyId/${sub?.survey?.limesurvey_id}/id/${sub?.response_id}/browseLang`;
  const audioLink = 'https://192.168.1.253/index.php?menu=monitoring';
  const telValue = sub?.payload_json?.TEL || sub?.payload_json?.Numero || 'N/A';
  const sessionCode = sub?.payload_json?.SessionCode || sub?.payload_json?.sessioncode || 'N/A';
  const personName = isF2f ? (sub?.person?.name || 'N/A') : (sub?.interviewer?.name || 'N/A');
  const personRef = isF2f ? (sub?.person?.ref || 'N/A') : (sub?.interviewer?.ref || 'N/A');
  const isSupervisor = isF2f && sub?.person_type === 'supervisor';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Back Button & Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faPause} className="text-yellow-500" />
            Reprendre le Controle (Resp. Controle)
          </h1>
          <p className="text-gray-600 mt-1">Vous reprenez cette soumission depuis la liste "Mis en Attente"</p>
        </div>

        {/* Previous hold info */}
        <div className="mb-4 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800">
          <FontAwesomeIcon icon={faPause} className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-medium block">Mise en attente precedente</span>
            <span className="text-sm">
              Le {new Date(check.checked_at).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
              {check.raison && ` - Raison: ${check.raison}`}
            </span>
            {check.comment && (
              <span className="text-sm block mt-1 italic">"{check.comment}"</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Survey Info & Links */}
          <div className="lg:col-span-2 space-y-4">
            {/* Assignment Info Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Informations
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Enquete</p>
                  <p className="font-medium text-gray-800">{sub?.survey?.title}</p>
                  <p className="text-xs text-gray-500 mt-1">ID: {sub?.survey?.limesurvey_id || 'N/A'}</p>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1 mt-3">ID de la reponse</p>
                  <p className="font-medium text-gray-800">{sub?.response_id || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                    {isSupervisor ? 'Prestataire' : 'Enqueteur'}
                  </p>
                  <p className="font-medium text-gray-800">{personName}</p>
                  <p className="text-xs text-gray-500">Ref: {personRef}</p>
                  {isSupervisor && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                      Prestataire
                    </span>
                  )}
                </div>
                {isF2f ? (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Code Session</p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <p className="font-medium text-gray-800 break-words min-w-0 flex-1">{sessionCode}</p>
                      {sessionCode && sessionCode !== 'N/A' && (
                        <button
                          onClick={handleCopyTel}
                          className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors flex-shrink-0 w-full sm:w-auto"
                          title="Copier le code"
                        >
                          {copiedTel ? 'Copie' : 'Copier'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Telephone</p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <p className="font-medium text-gray-800 break-words min-w-0 flex-1">{telValue}</p>
                      {telValue && telValue !== 'N/A' && (
                        <button
                          onClick={handleCopyTel}
                          className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors flex-shrink-0 w-full sm:w-auto"
                          title="Copier le numero"
                        >
                          {copiedTel ? 'Copie' : 'Copier'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Survey & Audio Links Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Acces
              </h2>
              <div className="space-y-3">
                <a
                  href={surveyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all group"
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-red-200 transition-colors">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 group-hover:text-red-700 transition-colors">Questionnaire LimeSurvey</p>
                      <p className="text-xs text-gray-500">Ouvre dans un nouvel onglet</p>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>

                {isF2f ? (
                  sessionCode && sessionCode !== 'N/A' && (
                    <a
                      href={`https://fichiers.immersion.ma:4446/survey/${sub?.survey?.limesurvey_id}/${sessionCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all group"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-red-200 transition-colors">
                          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 group-hover:text-red-700 transition-colors">Monitoring Fichiers</p>
                          <p className="text-xs text-gray-500">Session: {sessionCode}</p>
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )
                ) : (
                  <a
                    href={audioLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all group"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-red-200 transition-colors">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 group-hover:text-red-700 transition-colors">Monitoring Audio</p>
                        <p className="text-xs text-gray-500">TEL: {telValue}</p>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Evaluation Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                Evaluation
              </h2>

              {/* Rating */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Note sur 5 etoiles {(formData.action === 'validate' || formData.action === 'reject') && <span className="text-red-600">*</span>}
                </label>
                <div className="flex gap-1 justify-center mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className={`text-4xl outline-none focus:outline-none ${
                        star <= formData.rating && formData.rating > 0
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      } hover:scale-110 transition-all duration-200`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-600 font-medium">
                  {formData.rating > 0 ? `${formData.rating}/5` : 'Selectionnez une note'}
                </p>
              </div>

              {/* Raison */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Raison <span className="text-red-600">*</span>
                </label>
                <select
                  value={formData.raison}
                  onChange={(e) => setFormData({ ...formData, raison: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  required
                >
                  <option value="">Selectionnez une raison</option>
                  <option value="metier">Metier</option>
                  <option value="projet">Projet</option>
                  <option value="metier_et_projet">Metier et Projet</option>
                  <option value="rien">Rien</option>
                </select>
              </div>

              {/* Comment */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commentaire {formData.action === 'validate' && formData.correction_applied && <span className="text-red-600">*</span>}
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Ajoutez vos observations..."
                />
              </div>

              {/* Correction Applied */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Correction appliquee ? {formData.action === 'validate' && <span className="text-red-600">*</span>}
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.correction_applied === true
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                  }`}>
                    <input
                      type="radio"
                      name="correction_applied"
                      checked={formData.correction_applied === true}
                      onChange={() => setFormData({ ...formData, correction_applied: true })}
                      className="text-green-600 focus:ring-green-500 w-4 h-4"
                    />
                    <span className="ml-3 text-sm font-medium text-green-700">Oui</span>
                  </label>

                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.correction_applied === false
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                  }`}>
                    <input
                      type="radio"
                      name="correction_applied"
                      checked={formData.correction_applied === false}
                      onChange={() => setFormData({ ...formData, correction_applied: false })}
                      className="text-red-600 focus:ring-red-500 w-4 h-4"
                    />
                    <span className="ml-3 text-sm font-medium text-red-700">Non</span>
                  </label>
                </div>
              </div>

              {/* Action */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Action *
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.action === 'validate'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                  }`}>
                    <input
                      type="radio"
                      value="validate"
                      checked={formData.action === 'validate'}
                      onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                      className="text-green-600 focus:ring-green-500 w-4 h-4"
                    />
                    <span className="ml-3 text-sm font-medium text-green-700">Valider</span>
                  </label>

                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.action === 'reject'
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                  }`}>
                    <input
                      type="radio"
                      value="reject"
                      checked={formData.action === 'reject'}
                      onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                      className="text-red-600 focus:ring-red-500 w-4 h-4"
                    />
                    <span className="ml-3 text-sm font-medium text-red-700">Rejeter</span>
                  </label>

                  <label className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    formData.action === 'hold'
                      ? 'border-yellow-500 bg-yellow-50'
                      : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
                  }`}>
                    <input
                      type="radio"
                      value="hold"
                      checked={formData.action === 'hold'}
                      onChange={(e) => setFormData({ ...formData, action: e.target.value })}
                      className="text-yellow-600 focus:ring-yellow-500 w-4 h-4"
                    />
                    <span className="ml-3 text-sm font-medium text-yellow-700">Mettre en attente</span>
                  </label>
                </div>
              </div>

              {/* Reject Reason */}
              {formData.action === 'reject' && (
                <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
                  <label className="block text-sm font-medium text-red-700 mb-2">
                    Motif du rejet *
                  </label>
                  <textarea
                    value={formData.reject_reason}
                    onChange={(e) => setFormData({ ...formData, reject_reason: e.target.value })}
                    rows="3"
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
                    placeholder="Expliquez pourquoi vous rejetez ce questionnaire..."
                  />
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handlePreSubmit}
                disabled={submitting}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors shadow-sm hover:shadow-md"
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Soumission...
                  </span>
                ) : (
                  'Soumettre'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 transform transition-all animate-fadeIn">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Succes !</h3>
              <p className="text-gray-600 mb-4">
                Controle soumis avec succes
              </p>
              <div className="flex items-center justify-center text-sm text-gray-500">
                <svg className="animate-spin h-4 w-4 mr-2 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Retour a la liste...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Confirmation</h3>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir{' '}
                {formData.action === 'validate' && <span className="font-bold text-green-600">valider</span>}
                {formData.action === 'reject' && <span className="font-bold text-red-600">rejeter</span>}
                {formData.action === 'hold' && <span className="font-bold text-yellow-600">mettre en attente</span>}
                {' '}cette soumission ?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  className={`flex-1 py-2 px-4 text-white rounded-lg transition-colors ${
                    formData.action === 'validate' ? 'bg-green-600 hover:bg-green-700' :
                    formData.action === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-yellow-500 hover:bg-yellow-600'
                  }`}
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md mx-4 transform transition-all animate-fadeIn">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h3>
              <p className="text-gray-600 mb-6 whitespace-pre-line">
                {errorModalMessage}
              </p>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorModalMessage('');
                }}
                className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RespControleOnHoldDetail;
